import React, { useState, useEffect, useRef } from 'react';
import { Modal, ModalBody } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import config from '../../config';
import NewBooking from './NewBooking';
import { useChat } from '../../contexts/ChatContext';
import { getCustomerDisplayName } from '../../utils/customerUtils';
import styles from './StartConversationModal.module.scss';
import '../conversations/ConversationDetailsModal.css';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// ============================================================================
// WHATSAPP MARKDOWN PARSER
// ============================================================================
const parseWhatsAppMarkdown = (text) => {
  if (!text || typeof text !== 'string') return text;

  const tokenize = (str) => {
    const pattern = /(\*[^*\n]+\*|_[^_\n]+_|~[^~\n]+~|`[^`\n]+`)/g;
    const tokens = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(str)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({ type: 'text', value: str.slice(lastIndex, match.index) });
      }
      const raw = match[0];
      if (raw.startsWith('*'))       tokens.push({ type: 'bold',   value: raw.slice(1, -1) });
      else if (raw.startsWith('_')) tokens.push({ type: 'italic', value: raw.slice(1, -1) });
      else if (raw.startsWith('~')) tokens.push({ type: 'strike', value: raw.slice(1, -1) });
      else if (raw.startsWith('`')) tokens.push({ type: 'code',   value: raw.slice(1, -1) });
      lastIndex = match.index + raw.length;
    }

    if (lastIndex < str.length) {
      tokens.push({ type: 'text', value: str.slice(lastIndex) });
    }
    return tokens;
  };

  const renderToken = (token, idx) => {
    switch (token.type) {
      case 'bold':   return <strong key={idx}>{token.value}</strong>;
      case 'italic': return <em key={idx}>{token.value}</em>;
      case 'strike': return <s key={idx}>{token.value}</s>;
      case 'code':   return <code key={idx} style={{ fontFamily: 'monospace', background: 'rgba(0,0,0,0.08)', borderRadius: '3px', padding: '0 3px' }}>{token.value}</code>;
      default:       return token.value;
    }
  };

  const lines = text.split('\n');
  return lines.map((line, lineIdx) => (
    <React.Fragment key={lineIdx}>
      {tokenize(line).map((token, tokenIdx) => renderToken(token, tokenIdx))}
      {lineIdx < lines.length - 1 && <br />}
    </React.Fragment>
  ));
};
const LoadingState = () => (
  <div className={styles.loadingState}>
    <div className={styles.spinner}></div>
    <p>Loading customer details...</p>
  </div>
);

const ErrorState = ({ error, onRetry }) => (
  <div className={styles.errorState}>
    <i className={`fa fa-exclamation-triangle ${styles.errorIcon}`}></i>
    <div className={styles.errorContent}>
      <h6>Failed to load customer</h6>
      <p>{error}</p>
    </div>
    {onRetry && (
      <button className={styles.retryBtn} onClick={onRetry}>
        <i className="fa fa-refresh"></i> Retry
      </button>
    )}
  </div>
);

const InfoRow = ({ label, value, renderValue }) => (
  <tr>
    <td className={styles.infoLabel}>{label}</td>
    <td className={styles.infoValue}>{renderValue ? renderValue() : (value || 'N/A')}</td>
  </tr>
);

// Message Bubble Component
const REPLY_TO_PREFIX_RE = /^\[REPLY_TO prior message:.*?\]\n?/s;
const REPLY_QUOTE_PREVIEW_CHARS = 140;

const stripReplyPrefix = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(REPLY_TO_PREFIX_RE, '').trim() || text;
};

const ExpandableReplyQuote = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;
  const full = String(text);
  const needsExpand = full.length > REPLY_QUOTE_PREVIEW_CHARS;
  const shown = !needsExpand || expanded
    ? full
    : `${full.slice(0, REPLY_QUOTE_PREVIEW_CHARS).trimEnd()}…`;

  return (
    <div
      className={`${styles.replyQuote}${needsExpand ? ` ${styles.replyQuoteClickable}` : ''}`}
      onClick={needsExpand ? () => setExpanded((v) => !v) : undefined}
      role={needsExpand ? 'button' : undefined}
      tabIndex={needsExpand ? 0 : undefined}
      onKeyDown={
        needsExpand
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setExpanded((v) => !v);
              }
            }
          : undefined
      }
      title={needsExpand ? (expanded ? 'Click to collapse' : 'Click to show full quoted message') : undefined}
    >
      <div className={styles.replyQuoteLabel}>Replying to</div>
      <div className={styles.replyQuoteBody}>{shown}</div>
      {needsExpand && (
        <div className={styles.replyQuoteToggle}>
          {expanded ? 'Show less' : 'Show full message'}
        </div>
      )}
    </div>
  );
};

const MessageBubble = ({ message, index }) => {
  const isNotification = message.type === 'notification' || message.type === 'notification_reply';
  const isBot = message.role === 'assistant' || message.role === 'bot';
  const isOperator = message.role === 'operator';
  const isUser = message.role === 'user';
  const isFailed = message.status === 'failed';

  const renderMessageContent = () => {
    const mediaBase = `${config.customerApiBase}/media`;
    const displayContent = stripReplyPrefix(message.content);
    if (message.message_type === 'image' && message.media_id) {
      return (
        <>
          <img
            src={`${mediaBase}/${message.media_id}`}
            alt={displayContent && displayContent !== '[Image]' ? displayContent : 'Image'}
            style={{ maxWidth: '100%', borderRadius: 8, display: 'block', cursor: 'pointer' }}
            onClick={() => window.open(`${mediaBase}/${message.media_id}`, '_blank')}
          />
          {displayContent && displayContent !== '[Image]' && (
            <div style={{ marginTop: 4 }}>{parseWhatsAppMarkdown(displayContent)}</div>
          )}
        </>
      );
    }
    if (message.message_type === 'document' && message.media_id) {
      const fileName = message.filename || displayContent || 'Document';
      return (
        <a
          href={`${mediaBase}/${message.media_id}`}
          target="_blank"
          rel="noopener noreferrer"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
        >
          <i className="fa fa-file-o" />
          {fileName}
        </a>
      );
    }
    if (message.message_type === 'audio' && message.media_id) {
      return (
        <audio
          controls
          src={`${mediaBase}/${message.media_id}`}
          style={{ width: '100%', minWidth: 220 }}
        />
      );
    }
    return parseWhatsAppMarkdown(displayContent);
  };

  const quoteBlock = message.reply_to ? (
    <ExpandableReplyQuote text={message.reply_to} />
  ) : null;

  // Notifications get their own centred pill-style bubble regardless of role
  if (isNotification) {
    return (
      <div key={index} style={{ display: 'flex', justifyContent: 'center', margin: '6px 0' }}>
        <div className={`${styles.messageBubble} ${styles.notification}`}>
          <div className={styles.messageRole}>
            <i className="fa fa-bell"></i>
            {message.type === 'notification_reply' ? 'Customer Reply' : 'Notification'}
          </div>
          <div className={styles.messageContent}>{parseWhatsAppMarkdown(message.content)}</div>
          {message.timestamp && (
            <div className={styles.messageTime} style={{ textAlign: 'center' }}>
              {new Date(message.timestamp).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </div>
          )}
        </div>
      </div>
    );
  }

  let bubbleClass = styles.bot;
  let icon = 'fa-robot';
  let label = 'Bot';
  
  if (isOperator) {
    bubbleClass = styles.operator;
    icon = 'fa-headset';
    label = 'Operator';
  } else if (isUser) {
    bubbleClass = styles.customer;
    icon = 'fa-user';
    label = 'Customer';
  }
  
  return (
    <div 
      key={index}
      style={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start' }}
    >
      <div className={`${styles.messageBubble} ${bubbleClass}`}>
        <div className={styles.messageRole}>
          <i className={`fa ${icon}`}></i>
          {label}
        </div>
        {quoteBlock}
        <div className={styles.messageContent}>{renderMessageContent()}</div>
        <div className={styles.messageFooter} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
            {message.timestamp && (
            <div className={styles.messageTime}>
                {new Date(message.timestamp).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
                })}
            </div>
            )}
            {isFailed && (
                <div className="text-danger" title={message.error || "Message failed to send"} style={{ marginLeft: '8px' }}>
                    <i className="fa fa-exclamation-circle"></i>
                </div>
            )}
        </div>
      </div>
    </div>
  );
};

// Booking Card Component
const BookingCard = ({ booking }) => {
  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'success',
      'confirmed': 'primary',
      'cancelled': 'danger',
      'pending': 'warning',
      'Assigned': 'info',
      'in_progress': 'info'
    };
    const color = statusColors[status] || 'secondary';
    return <span className={`${styles.badge} ${styles[color]}`}>{status || 'unknown'}</span>;
  };

  const isPaid = booking.payment_id || booking.payment_status === 'paid';

  return (
    <div className={styles.bookingCard}>
      <div className={styles.bookingHeader}>
        <div className={styles.bookingIds}>
          <span className={styles.bookingId}>#{booking.id}</span>
        </div>
        {getStatusBadge(booking.state)}
      </div>
      <div className={styles.bookingLocations}>
        <div className={styles.locationRow}>
          <i className="fa fa-dot-circle-o" style={{ color: '#28a745' }}></i>
          <span>{booking.pickup_location || 'N/A'}</span>
        </div>
        <div className={styles.locationRow}>
          <i className="fa fa-map-marker" style={{ color: '#dc3545' }}></i>
          <span>{booking.drop_location || 'N/A'}</span>
        </div>
        {(booking.vehicle_class || booking.vehicle_class_id) && (
          <div className={styles.vehicleRow}>
            <i className="fa fa-truck" style={{ color: '#6c757d' }}></i>
            <span>{booking.vehicle_class || `Class ${booking.vehicle_class_id}`}</span>
          </div>
        )}
      </div>
      <div className={styles.bookingMeta}>
        <span className={styles.bookingDate}>
          <i className="fa fa-clock-o"></i>
          {formatDateTime(booking.pickup_time)}
        </span>
        {booking.payment_estimate && (
          <span className={`${styles.bookingPrice} ${isPaid ? styles.paid : styles.unpaid}`}>
            ₹{booking.payment_estimate}
            {isPaid ? <i className="fa fa-check-circle" style={{ marginLeft: '4px' }}></i> : null}
          </span>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const StartConversationModal = ({ isOpen, toggle, onMinimize, isMinimized = false, customerId, userId, isJoining = false }) => {
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isBlocked, setIsBlocked] = useState(false);
  const [messageInput, setMessageInput] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const imageInputRef = useRef(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [sessionPhone, setSessionPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const history = useHistory();
  const wsRef = useRef(null);
  const messagesEndRef = useRef(null);
  const prevIsOpenRef = useRef(false);

  // Unread counter kept in ChatContext — no WS logic here for unread
  const { incrementUnread: _unused } = useChat(); // keep import live; actual tracking is in ChatContext

  // Session sockets are keyed by PHONE on backend. Never prefer DB user_id for session WS keys.
  const sessionWsKey = sessionPhone || customerDetails?.customer?.phone || null;

  const handleMinimize = () => {
    if (onMinimize) {
      const wsProtocol = config.customerApiUrl.startsWith('https') ? 'wss' : 'ws';
      const wsBase = config.customerApiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      let wsUrl = null;
      if (currentConversationId) {
        wsUrl = `${wsProtocol}://${wsBase}/ws/conversations/${currentConversationId}`;
      } else if (isJoining && sessionWsKey) {
        const sessionKey = sessionWsKey;
        wsUrl = `${wsProtocol}://${wsBase}/ws/sessions/${sessionKey}`;
      }
      onMinimize({
        displayName: customerDetails?.customer?.name || null,
        displayPhone: customerDetails?.customer?.phone || null,
        wsUrl,
      });
    }
  };

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Close WebSocket when modal closes
  useEffect(() => {
    if (!isOpen) {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
        setWsConnected(false);
      }
    }
  }, [isOpen]);

  // Connect to WebSocket when conversation is created or joining active session
  useEffect(() => {
    if (!isOpen) return;

    let wsUrl = null;
    const wsProtocol = config.customerApiUrl.startsWith('https') ? 'wss' : 'ws';
    const wsBase = config.customerApiUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');

    if (currentConversationId) {
      wsUrl = `${wsProtocol}://${wsBase}/ws/conversations/${currentConversationId}`;
    } else if (isJoining && sessionWsKey) {
      const sessionKey = sessionWsKey;
      wsUrl = `${wsProtocol}://${wsBase}/ws/sessions/${sessionKey}`;
    }

    if (!wsUrl) return;

    console.log('Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setWsConnected(true);
      console.log('WebSocket connected');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'new_message') {
          console.log('Received new message via WebSocket:', data.message);

          const content = data.message.content ? data.message.content.toString() : '';
          if (content.startsWith('[TOOL]:') || content.includes('<ToolCall>')) return;

          setMessages(prev => {
            const pendingIndex = prev.findIndex(msg =>
              !msg.id &&
              msg.role === data.message.role &&
              msg.content === data.message.content
            );
            if (pendingIndex !== -1) {
              const newMessages = [...prev];
              newMessages[pendingIndex] = data.message;
              return newMessages;
            }
            const messageExists = prev.some(
              msg => (data.message.id && msg.id === data.message.id) ||
                     (msg.content === data.message.content && msg.timestamp === data.message.timestamp)
            );
            if (messageExists) return prev;
            return [...prev, data.message];
          });
        } else if (data.type === 'message_status') {
          console.log('Received message status update:', data);
          setMessages(prev => {
            let updated = false;
            const next = prev.map(msg => {
              if (msg.id === data.message_id) {
                updated = true;
                return { ...msg, status: data.status, error: data.error };
              }
              return msg;
            });
            if (!updated) {
              for (let i = prev.length - 1; i >= 0; i -= 1) {
                const msg = prev[i];
                if (msg.role === 'operator' && !msg.id) {
                  next[i] = { ...msg, status: data.status, error: data.error };
                  updated = true;
                  break;
                }
              }
            }
            return updated ? next : prev;
          });
        }
      } catch (err) {
        console.error('Error parsing WebSocket message:', err);
      }
    };

    ws.onerror = (error) => { console.error('WebSocket error:', error); setWsConnected(false); };
    ws.onclose = () => { setWsConnected(false); console.log('WebSocket closed'); };

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, [isOpen, currentConversationId, isJoining, sessionWsKey]);

  const fetchCustomerDetails = async () => {
    if (!customerId) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`${config.customerApiBase}/customers/${customerId}/details`);
      if (!response.ok) {
        throw new Error(`Failed to fetch customer: ${response.status}`);
      }
      
      const data = await response.json();
      setCustomerDetails(data);
      setIsBlocked(data.customer?.is_blocked || false);
    } catch (err) {
      console.error('Error fetching customer:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  // Fetch the latest conversation for the customer (for joining existing conversations)
  const fetchLatestConversation = async () => {
    try {
      const userIdToUse = customerDetails?.customer?.user_id || userId;
      
      // If joining an active session, get the live session history instead of DB conversations
      if (isJoining && userIdToUse) {
        try {
          const sessionResponse = await fetch(`${config.customerApiBase}/sessions/${userIdToUse}/history`);
          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();
            
            // Store phone number for WebSocket connection
            if (sessionData.user_phone) {
              setSessionPhone(sessionData.user_phone);
            }

            // Backend returns 'messages', but check 'history' for backward compatibility
            const sessionMessages = sessionData.messages || sessionData.history || [];
            
            console.log('Raw session messages:', sessionMessages); // Debug log

            // Filter out internal tool messages (client-side safety)
            const filteredMessages = sessionMessages.filter(msg => {
              const content = msg.content ? msg.content.toString() : '';
              return !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
            });
            
            setMessages(filteredMessages);
            console.log(`Loaded ${filteredMessages.length} messages from active session`);
            
            // Don't set conversation ID - we'll create a new one when ops sends a message
            setCurrentConversationId(null);
            return;
          }
        } catch (sessionErr) {
          console.log('No active session found, falling back to DB conversations');
        }
      }
      
      // Fallback: Get from database conversations
      const response = await fetch(`${config.customerApiBase}/customers/${customerId}/conversations`);
      if (!response.ok) {
        console.error('Failed to fetch conversations');
        return;
      }
      
      const data = await response.json();
      const conversations = data.conversations || [];
      
      if (conversations.length > 0) {
        // Get the latest conversation
        const latestConv = conversations[0];
        setCurrentConversationId(latestConv.id);
        
        // Load messages from this conversation
        const convMessages = latestConv.messages || [];
        setMessages(convMessages);
        console.log(`Loaded ${convMessages.length} messages from conversation ${latestConv.id}`);
      }
    } catch (err) {
      console.error('Error fetching latest conversation:', err);
    }
  };

  // When restoring from minimized state, fetch latest data once to backfill
  // any messages that arrived while the modal was closed.
  const syncMissedMessagesOnRestore = async () => {
    try {
      // If a DB conversation exists, refresh from conversation endpoint
      if (currentConversationId) {
        const resp = await fetch(`${config.customerApiBase}/conversations/${currentConversationId}`);
        if (!resp.ok) return;
        const data = await resp.json();
        const serverMessages = data?.conversation?.messages || [];
        setMessages(prev => {
          const merged = [...(prev || [])];
          serverMessages.forEach((msg) => {
            const exists = merged.some(existing =>
              (msg.id && existing.id && msg.id === existing.id) ||
              (existing.content === msg.content && existing.timestamp === msg.timestamp && existing.role === msg.role)
            );
            if (!exists) merged.push(msg);
          });
          return merged;
        });
        return;
      }

      // For join/live session (no conversation yet), refresh session history
      if (isJoining) {
        const userIdToUse = customerDetails?.customer?.user_id || userId;
        if (!userIdToUse) return;
        const resp = await fetch(`${config.customerApiBase}/sessions/${userIdToUse}/history`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (data.user_phone) setSessionPhone(data.user_phone);

        const sessionMessages = data.messages || data.history || [];
        const filteredMessages = sessionMessages.filter(msg => {
          const content = msg.content ? msg.content.toString() : '';
          return !content.startsWith('[TOOL]:') && !content.includes('<ToolCall>');
        });

        setMessages(prev => {
          const merged = [...(prev || [])];
          filteredMessages.forEach((msg) => {
            const exists = merged.some(existing =>
              (msg.id && existing.id && msg.id === existing.id) ||
              (existing.content === msg.content && existing.timestamp === msg.timestamp && existing.role === msg.role)
            );
            if (!exists) merged.push(msg);
          });
          return merged;
        });
      }
    } catch (err) {
      console.error('Error syncing missed messages on restore:', err);
    }
  };

  // Reset state when modal truly closes (NOT when minimizing)
  useEffect(() => {
    if (!isOpen && !isMinimized) {
      setCustomerDetails(null);
      setCurrentConversationId(null);
      setSessionPhone(null);
      setMessages([]);
      setShowBookingForm(false);
      setMessageInput('');
      setSelectedImage(null);
      setIsBlocked(false);
      setError(null);
      setLoading(false);
    }
  }, [isOpen, isMinimized]);
  
  // Fetch customer details when modal opens
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && customerId) {
      // If restoring from minimize and data is still intact, skip re-fetch
      if (prevOpenRef.current === false && customerDetails) {
        prevOpenRef.current = true;
        return;
      }
      prevOpenRef.current = true;

      setCustomerDetails(null);
      setMessages([]);
      setCurrentConversationId(null);
      setSessionPhone(null);
      fetchCustomerDetails();
    }
    if (!isOpen) {
      prevOpenRef.current = false;
    }
  }, [isOpen, customerId]);

  // Backfill missed messages after restoring from minimized bubble.
  useEffect(() => {
    const wasOpen = prevIsOpenRef.current;
    if (isOpen && !wasOpen) {
      syncMissedMessagesOnRestore();
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen, currentConversationId, isJoining, customerDetails, userId]);

  // Fetch latest conversation when joining an existing chat (after customer details are loaded)
  useEffect(() => {
    if (isOpen && isJoining && customerId && customerDetails?.customer?.user_id) {
      // If we already have a conversation ID and messages (e.g. just started a chat), don't re-fetch
      // This prevents the "Start Chat" -> "Join Chat" transition from clearing the messages
      if (currentConversationId && messages.length > 0) {
        return;
      }
      fetchLatestConversation();
    }
  }, [isOpen, isJoining, customerId, customerDetails, currentConversationId, messages.length]);

  const handleBlockToggle = async () => {
    if (!customerId) return;
    
    // If currently blocked and trying to unblock, confirm ending the session
    if (isBlocked) {
      const confirmEnd = window.confirm(
        'This will unblock the user, end this conversation session, and re-enable the LLM bot.\n\nAre you sure you want to end this conversation?'
      );
      if (!confirmEnd) return;
    }
    
    try {
      const response = await fetch(`${config.customerApiBase}/customers/${customerId}/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_blocked: !isBlocked })
      });
      
      if (!response.ok) {
        throw new Error('Failed to update block status');
      }
      
      const data = await response.json();
      setIsBlocked(!isBlocked);
      
      // If we just unblocked (ended session), close the modal
      if (data.session_ended) {
        alert('Conversation ended. The user can now interact with the LLM bot again.');
        toggle(); // Close the modal
      }
    } catch (err) {
      console.error('Error toggling block:', err);
      alert('Failed to update block status');
    }
  };

  const handleSendMessage = async () => {
    if ((!messageInput.trim() && !selectedImage) || sending) return;
    if (!isBlocked) {
      alert('Please block the user first to send messages');
      return;
    }

    setSending(true);
    try {
      let convId = currentConversationId;
      
      // If no conversation exists, create one first
      if (!convId) {
        const userIdToUse = customerDetails?.customer?.user_id || userId;
        if (!userIdToUse) {
          throw new Error('No user ID available to create conversation');
        }
        
        const createResponse = await fetch(`${config.customerApiBase}/conversations/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            user_id: userIdToUse,
            customer_id: customerId,
            intent: isJoining ? 'ops_takeover' : 'ops_initiated',
            success: true,
            source: isJoining ? 'join_conversation_popup' : 'start_conversation_popup'
          })
        });
        
        if (!createResponse.ok) {
          throw new Error('Failed to create conversation');
        }
        
        const createData = await createResponse.json();
        convId = createData.conversation_id;
        setCurrentConversationId(convId);
      }
      
      // Send the message
      let response;
      if (selectedImage) {
        const formData = new FormData();
        if (messageInput.trim()) formData.append('message', messageInput);
        formData.append('file', selectedImage.file, selectedImage.name);
        response = await fetch(`${config.customerApiBase}/conversations/${convId}/ops-message`, {
          method: 'POST',
          body: formData
        });
      } else {
        response = await fetch(`${config.customerApiBase}/conversations/${convId}/ops-message`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message: messageInput })
        });
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || errorData.message || `Server error: ${response.status}`);
      }

      // Add message locally for immediate feedback
      // WebSocket may not be connected yet for first message, and deduplication handles any duplicates
      const newMessage = {
        role: 'operator',
        content: selectedImage ? (messageInput.trim() || '[Image]') : messageInput,
        timestamp: new Date().toISOString()
      };
      if (selectedImage) {
        newMessage.message_type = 'image';
      }
      setMessages(prev => {
        // Check if message already exists (from WebSocket)
        const messageExists = prev.some(
          msg => msg.content === newMessage.content && msg.role === 'operator'
        );
        if (messageExists) return prev;
        return [...prev, newMessage];
      });

      setMessageInput('');
      setSelectedImage(null);
    } catch (err) {
      console.error('Error sending message:', err);
      alert(`Failed to send message: ${err.message || 'Unknown error'}`);
    } finally {
      setSending(false);
    }
  };

  const handleAttachImage = () => {
    imageInputRef.current?.click();
  };

  const handleImageSelected = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImage({
        file,
        name: file.name,
        previewUrl: URL.createObjectURL(file)
      });
    }
    e.target.value = '';
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const customer = customerDetails?.customer;
  const bookings = customerDetails?.bookings || [];
  const recentBookings = bookings.slice(0, 5);

  return (
    <>
      {/* Main Modal */}
      <Modal 
        isOpen={isOpen} 
        toggle={toggle} 
        size="xl" 
        scrollable
        modalClassName={showBookingForm ? "start-conversation-modal-left" : "start-conversation-modal"}
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <i className={`fa ${isJoining ? 'fa-comments' : 'fa-comment'}`}></i>
          </div>
          <h5 className={styles.headerTitle}>{isJoining ? 'Join Conversation' : 'Start Conversation'}</h5>
          <div className={styles.headerActions}>
            {onMinimize && (
              <button
                className={styles.minimizeBtn}
                onClick={handleMinimize}
                title="Minimize — keep chat open while you browse"
              >
                <i className="fa fa-window-minimize"></i>
              </button>
            )}
              <button className={styles.closeBtn} onClick={toggle}>
              <i className="fa fa-times"></i>
            </button>
          </div>
        </div>
      
        <ModalBody className={styles.modalBody}>
          {loading && <LoadingState />}
          {error && <ErrorState error={error} onRetry={fetchCustomerDetails} />}
        
          {!loading && !error && customerDetails && (
            <div className={styles.contentLayout}>
              {/* Left Panel - Customer Info & Bookings */}
              <div className={styles.leftPanel}>
                {/* Customer Info Card */}
                <div className={styles.customerCard}>
                  <div className={styles.customerHeader}>
                    <div className={styles.customerAvatar}>
                      <i className="fa fa-user"></i>
                    </div>
                    <div className={styles.customerInfo}>
                      <h4 className={styles.customerName}>{getCustomerDisplayName(customer?.name, customer?.business_name, customer?.phone, 'Unknown')}</h4>
                      <div className={styles.customerPhone}>
                        <i className="fa fa-phone"></i>
                        {customer?.phone || 'N/A'}
                      </div>
                    </div>
                    <div className={styles.customerStats}>
                      <div className={styles.statItem}>
                        <span className={styles.statValue}>{bookings.length}</span>
                        <span className={styles.statLabel}>Bookings</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className={styles.customerDetails}>
                    <table className={styles.infoTable}>
                      <tbody>
                        <InfoRow
                          label="Customer ID"
                          renderValue={() => (
                            <code style={{ padding: '0.25rem 0.5rem', background: '#f1f3f5', borderRadius: '4px' }}>
                              {customer?.customer_id || customer?.id || customerId || 'N/A'}
                            </code>
                          )}
                        />
                        <InfoRow label="Language" value={customer?.preferred_language || customer?.language || 'N/A'} />
                        <InfoRow label="Joined" value={formatDateTime(customer?.created_at)} />
                        {customer?.rating && (
                          <InfoRow 
                            label="Rating" 
                            renderValue={() => (
                              <span className={styles.ratingBadge}>
                                <i className="fa fa-star"></i> {customer.rating}
                              </span>
                            )}
                          />
                        )}
                        {customerDetails.primary_address && (
                          <InfoRow 
                            label="Address" 
                            renderValue={() => (
                              <div className={styles.inlineAddress}>
                                <div className={styles.addressLocality}>
                                  {customerDetails.primary_address.locality}
                                </div>
                                <div className={styles.addressDetails}>
                                  {[
                                    customerDetails.primary_address.city,
                                    customerDetails.primary_address.state,
                                    customerDetails.primary_address.pin_code
                                  ].filter(Boolean).join(', ')}
                                </div>
                              </div>
                            )}
                          />
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className={styles.customerActions}>
                    <button
                      className={styles.newBookingBtn}
                      onClick={() => setShowBookingForm(!showBookingForm)}
                    >
                      <i className={`fa ${showBookingForm ? 'fa-times' : 'fa-plus'}`}></i>
                      {showBookingForm ? 'Hide Booking' : 'New Booking'}
                    </button>
                  </div>
                </div>

                {/* Previous Bookings */}
                <div className={styles.bookingsSection}>
                  <div className={styles.sectionHeader}>
                    <i className="fa fa-calendar"></i>
                    <h5>Previous Bookings</h5>
                    {bookings.length > 0 && (
                      <span className={styles.sectionBadge}>{bookings.length}</span>
                    )}
                  </div>

                  {recentBookings.length > 0 ? (
                    <div className={styles.bookingsList}>
                      {recentBookings.map((booking) => (
                        <BookingCard key={booking.id} booking={booking} />
                      ))}
                      {bookings.length > 5 && (
                        <div className={styles.showMoreHint}>
                          <i className="fa fa-info-circle"></i>
                          Showing 5 of {bookings.length} bookings
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className={styles.emptyBookings}>
                      <i className="fa fa-calendar-o"></i>
                      <p>No previous bookings</p>
                    </div>
                  )}
                </div>

              </div>

              {/* Right Panel - Chat */}
              <div className={styles.rightPanel}>
                <div className={styles.chatCard}>
                  {/* Chat Header */}
                  <div className={styles.chatHeader}>
                    <div className={styles.chatHeaderLeft}>
                      <i className="fa fa-comments"></i>
                      <span>New Conversation</span>
                    </div>
                    <div className={styles.chatHeaderRight}>
                      <span className={`${styles.statusBadge} ${isBlocked ? styles.blocked : styles.active}`}>
                        {isBlocked ? 'User Blocked' : 'LLM Active'}
                      </span>
                      {wsConnected && (
                        <span className={`${styles.statusBadge} ${styles.connected}`}>
                          <span className={styles.connectedDot}></span>
                          Connected
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Block/Unblock Button */}
                  <div className={styles.blockSection}>
                    <button
                      className={`${styles.blockBtn} ${isBlocked ? styles.unblock : styles.block}`}
                      onClick={handleBlockToggle}
                    >
                      <i className={`fa ${isBlocked ? 'fa-sign-out' : 'fa-lock'}`}></i>
                      {isBlocked ? 'End Conversation & Unblock' : 'Block User to Start Chatting'}
                    </button>
                    {!isBlocked && (
                      <p className={styles.blockHint}>
                        <i className="fa fa-info-circle"></i>
                        Block the user to disable the LLM bot and take over the conversation
                      </p>
                    )}
                    {isBlocked && (
                      <p className={styles.blockHint}>
                        <i className="fa fa-info-circle"></i>
                        Ending the conversation will unblock the user and re-enable the LLM bot
                      </p>
                    )}
                  </div>

                  {/* Messages Container */}
                  <div className={styles.messagesContainer}>
                    {messages.length === 0 ? (
                      <div className={styles.emptyChat}>
                        <div className={styles.emptyChatIcon}>
                          <i className="fa fa-comment-o"></i>
                        </div>
                        <h6>Start a new conversation</h6>
                        <p>
                          {isBlocked 
                            ? 'Type a message below to start the conversation'
                            : 'Block the user first, then send your message'}
                        </p>
                      </div>
                    ) : (
                      <>
                        {messages.map((msg, index) => (
                          <MessageBubble key={index} message={msg} index={index} />
                        ))}
                        <div ref={messagesEndRef} />
                      </>
                    )}
                  </div>

                  {/* Message Input */}
                  <div className={styles.inputArea}>
                    {selectedImage && (
                      <div className={styles.imagePreview}>
                        <img
                          src={selectedImage.previewUrl}
                          alt=""
                          className={styles.imagePreviewImg}
                        />
                        <span className={styles.imagePreviewName}>{selectedImage.name}</span>
                        <button
                          className={styles.imagePreviewRemove}
                          onClick={handleRemoveImage}
                          title="Remove image"
                        >
                          <i className="fa fa-times"></i>
                        </button>
                      </div>
                    )}
                    <div className={styles.inputWrapper}>
                      <textarea
                        className={styles.messageInput}
                        placeholder={isBlocked ? "Type a message... (Shift+Enter for new line)" : "Block user first to send messages"}
                        value={messageInput}
                        onChange={(e) => {
                          setMessageInput(e.target.value);
                          e.target.style.height = 'auto';
                          e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                        disabled={!isBlocked || sending}
                        rows={1}
                      />
                      <div className={styles.formatHint}>
                        <span className={styles.formatLabel}>WhatsApp formatting:</span>
                        <code>*bold*</code>
                        <code>_italic_</code>
                        <code>~strike~</code>
                      </div>
                    </div>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleImageSelected}
                      style={{ display: 'none' }}
                    />
                    <button
                      className={styles.attachBtn}
                      onClick={handleAttachImage}
                      disabled={!isBlocked || sending}
                      title="Attach image"
                    >
                      <i className="fa fa-paperclip"></i>
                    </button>
                    <button
                      className={styles.sendBtn}
                      onClick={handleSendMessage}
                      disabled={!isBlocked || sending || (!messageInput.trim() && !selectedImage)}
                    >
                      {sending ? (
                        <i className="fa fa-spinner fa-spin"></i>
                      ) : (
                        <i className="fa fa-paper-plane"></i>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        
          {!loading && !error && !customerDetails && (
            <div className={styles.emptyState}>
              <i className={`fa fa-inbox ${styles.emptyIcon}`}></i>
              <p>Select a customer to start a conversation</p>
            </div>
          )}
        </ModalBody>
      </Modal>

      {/* Booking Modal - Appears to the right */}
      <Modal 
        isOpen={showBookingForm} 
        toggle={() => setShowBookingForm(false)} 
        size="lg"
        modalClassName="booking-side-modal"
        backdrop={false}
        fade={false}
      >
        <div className={styles.bookingModalHeader}>
          <div className={styles.headerIcon}>
            <i className="fa fa-calendar-plus"></i>
          </div>
          <h5 className={styles.headerTitle}>New Booking</h5>
          <button className={styles.closeBtn} onClick={() => setShowBookingForm(false)}>
            <i className="fa fa-times"></i>
          </button>
        </div>
        <ModalBody style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto', padding: '15px', background: '#f8f9fa' }}>
          <NewBooking 
            key={`${showBookingForm}-${customerId || ''}-${customer?.user_id || ''}-${customer?.phone || ''}`}
            compactMode={true}
            prefillUserId={customer?.user_id}
            prefillCustomerId={customerId}
            prefillUserName={customer?.name}
            prefillUserPhone={customer?.phone}
            onBookingCreated={() => {
              setShowBookingForm(false);
              fetchCustomerDetails();
            }}
          />
        </ModalBody>
      </Modal>
    </>
  );
};

export default StartConversationModal;
